import { graphql, fetchQuery, type Environment } from 'react-relay';
import { OPERAND_TYPE_FUNCTION, OPERAND_TYPE_KEYWORD, OPERAND_TYPE_VALUE } from '@atlaskit/jql-ast';
import type {
	EndCursor,
	OptionResponse,
	AvatarOption,
	SelectOption,
	SearchTemplate,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import type {
	peopleJqlBuilderBasicFieldValuesQuery,
	JiraJqlViewContext,
} from '@atlassian/jira-relay/src/__generated__/peopleJqlBuilderBasicFieldValuesQuery.graphql';
import type { peopleJqlBuilderBasicInitQuery } from '@atlassian/jira-relay/src/__generated__/peopleJqlBuilderBasicInitQuery.graphql';
import type { FormatMessage, CloudId } from '@atlassian/jira-shared-types/src/general.tsx';
import { OperandValues } from '../../../../../../common/constants.tsx';
import type { PropsResolver, PropTypes } from '../../../../../../common/types.tsx';
import {
	generateOptions,
	generateErrorOptions,
	fireJqlBuilderBasicErrorAnalytics,
} from '../../../../../../common/utils.tsx';
import { getEmptyLabel } from '../../../../../../utils/get-empty-label/index.tsx';
import { getFunctionLabel } from '../../../../../../utils/get-function-label/index.tsx';
import messages from './messages.tsx';

type Params = {
	fieldId: string;
	propType: PropTypes;
	formatMessage: FormatMessage;
	jql?: string;
	environment: Environment;
	cloudId: CloudId;
	operandDisplayValues?: string[];
	searchTemplate: SearchTemplate;
	viewContext: JiraJqlViewContext | null;
	suggestedUsersCount?: number;
};

type GetSuggestedParams = {
	environment: Environment;
	cloudId: CloudId;
	fieldId: string;
	searchTemplate: SearchTemplate;
	formatMessage: FormatMessage;
	suggestedUsersCount?: number;
};

type GetFieldValueParams = {
	environment: Environment;
	cloudId: CloudId;
	lowerCaseFieldId: string;
	jql?: string;
	search: string;
	endCursor: EndCursor;
	viewContext: JiraJqlViewContext | null;
};

const getSuggestedOptions = async ({
	environment,
	cloudId,
	searchTemplate,
	formatMessage,
	suggestedUsersCount = 5,
}: GetSuggestedParams): Promise<OptionResponse> => {
	const data = await fetchQuery<peopleJqlBuilderBasicInitQuery>(
		environment,
		graphql`
			query peopleJqlBuilderBasicInitQuery($cloudId: ID!, $suggestedUsersCount: Int) {
				me {
					user {
						picture
					}
				}
				jira @required(action: THROW) {
					jqlBuilder(cloudId: $cloudId) {
						recentlyUsedUsers(first: $suggestedUsersCount) {
							edges {
								node {
									jqlTerm
									displayName
									user {
										picture
									}
								}
							}
						}
						suggestedGroups(first: 5) {
							edges {
								node {
									jqlTerm
									displayName
								}
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
			suggestedUsersCount,
		},
		{ fetchPolicy: 'store-or-network' },
	).toPromise();
	if (!data?.jira?.jqlBuilder?.recentlyUsedUsers?.edges) {
		return generateOptions([]);
	}
	const users = data?.jira?.jqlBuilder?.recentlyUsedUsers?.edges.reduce<Array<AvatarOption>>(
		(options, edges) => {
			if (!edges || !edges.node) {
				return options;
			}
			const { node } = edges;
			options.push({
				label: node.displayName,
				value: node.jqlTerm,
				optionType: 'avatar',
				avatar: node.user?.picture,
				itemType: OPERAND_TYPE_VALUE,
			});
			return options;
		},
		[],
	);
	const groups =
		data?.jira?.jqlBuilder?.suggestedGroups?.edges?.reduce<Array<AvatarOption>>(
			(options, edges) => {
				if (!edges || !edges.node) {
					return options;
				}
				const { node } = edges;
				options.push({
					label: node.displayName,
					value: node.jqlTerm,
					optionType: 'avatar',
					isGroup: true,
					itemType: OPERAND_TYPE_FUNCTION,
				});
				return options;
			},
			[],
		) || [];

	const emptyLabel = getEmptyLabel(searchTemplate ?? null, formatMessage);

	const options: SelectOption[] = [
		{
			label: getFunctionLabel('currentUser', formatMessage),
			value: OperandValues.currentUser.value,
			optionType: 'avatar',
			avatar: data?.me?.user?.picture,
			itemType: OPERAND_TYPE_FUNCTION,
		},
		{
			label: emptyLabel,
			optionType: 'avatar',
			value: OperandValues.empty.value,
			itemType: OPERAND_TYPE_KEYWORD,
		},
		{
			label: formatMessage(messages.refinementOptionSuggestedUsers),
			optionType: 'group',
			options: users,
		},
	];

	options.push({
		label: formatMessage(messages.refinementOptionSuggestedGroups),
		optionType: 'group',
		options: groups,
	});

	return generateOptions(options);
};
const getFieldValueOptions = async ({
	environment,
	cloudId,
	lowerCaseFieldId,
	jql,
	search,
	endCursor,
	viewContext,
}: GetFieldValueParams): Promise<OptionResponse> => {
	const after = endCursor;

	const data = await fetchQuery<peopleJqlBuilderBasicFieldValuesQuery>(
		environment,
		graphql`
			query peopleJqlBuilderBasicFieldValuesQuery(
				$cloudId: ID!
				$first: Int = 10
				$jqlTerm: String!
				$jql: String!
				$searchString: String!
				$after: String
				$viewContext: JiraJqlViewContext
			) {
				jira @required(action: THROW) {
					jqlBuilder(cloudId: $cloudId) {
						fieldValues(
							first: $first
							jqlTerm: $jqlTerm
							jqlContext: $jql
							searchString: $searchString
							after: $after
							viewContext: $viewContext
						) {
							totalCount
							pageInfo {
								endCursor
							}
							edges {
								node {
									__typename
									jqlTerm
									displayName
									... on JiraJqlUserFieldValue {
										user {
											picture
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
			first: 10,
			jqlTerm: lowerCaseFieldId,
			jql: jql || '',
			searchString: search,
			after,
			viewContext,
		},
		{ fetchPolicy: 'store-or-network' },
	).toPromise();
	if (!data?.jira?.jqlBuilder?.fieldValues?.edges) {
		return generateOptions([]);
	}

	const { edges: fieldValues, totalCount, pageInfo } = data.jira.jqlBuilder.fieldValues;
	return generateOptions(
		fieldValues.reduce<Array<AvatarOption>>((options, edges) => {
			if (!edges || !edges.node) {
				return options;
			}

			const { node } = edges;
			options.push({
				label: node.displayName,
				value: node.jqlTerm,
				optionType: 'avatar',
				avatar: node.user?.picture,
				...(node.__typename === 'JiraJqlGroupFieldValue'
					? { isGroup: true, itemType: OPERAND_TYPE_FUNCTION }
					: {
							itemType: OPERAND_TYPE_VALUE,
						}),
			});
			return options;
		}, []),
		totalCount,
		pageInfo?.endCursor || undefined,
	);
};

// (Note: there may be an external usage of this function here: src/packages/servicedesk/change-management/change-calendar/src/ui/filter-bar/assignee-filter/index.tsx)
export const getPeopleProps = ({
	fieldId,
	formatMessage,
	jql,
	environment,
	cloudId,
	operandDisplayValues,
	searchTemplate,
	viewContext,
	suggestedUsersCount,
}: Params): PropsResolver => ({
	enableFullList: true,
	operandDisplayValues,
	loadOptions: async (search, renderInitialValues, endCursor) => {
		try {
			if (search.length === 0 && renderInitialValues) {
				return await getSuggestedOptions({
					environment,
					cloudId,
					fieldId,
					searchTemplate,
					formatMessage,
					suggestedUsersCount,
				});
			}
			return await getFieldValueOptions({
				environment,
				cloudId,
				lowerCaseFieldId: fieldId.toLowerCase(),
				jql,
				search,
				endCursor,
				viewContext,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireJqlBuilderBasicErrorAnalytics('peopleJqlBuilderBasicFieldValues', searchTemplate, error);
			return generateErrorOptions(error);
		}
	},
});
